import { EM_DASH } from 'constants/index';
import { AutomaticControlType } from 'store/modules/controlPoints/types';
import { LatLong } from 'types/asset';
import { NON_CONSOLIDATED_WIRELESS_DEVICE_ID } from 'types/device-topology';
import { DeviceStatus, DeviceStatusCause } from 'types/device.enum';
import { AssetTypeId, RawAssetTypeCode } from 'types/models/asset-type';
import Device, { DeviceId, DeviceModel } from 'types/models/device';
import { PowerLine, PowerMeterChannel, PowerMeterStatus } from 'types/models/power-meter';
import Sample from 'types/models/sample';
import { HeaterSummary, MachineControlStatistic, RainSummary, SoilSummary } from 'types/models/samplePointsStatistic';
import Site, { SiteId } from 'types/models/site';
import { TankShape } from 'types/tank-shape.enum';
import { Quantity } from 'utils/convert-unit';
import { Modify } from 'utils/TypeScript/modify';

export type SamplePointId = number;
export type SamplePointSid = string;
export type Trend = number;
export enum LevelDisplayUnit {
  CM = 'cm',
  IN = 'in',
  M = 'm',
  FT = 'ft'
}
export enum VolumeDisplayUnit {
  L = 'L',
  KL = 'KL',
  ML = 'ML',
  GAL = 'gal',
  CU_FT = 'cu. ft.',
  AC_FT = 'ac. ft.'
}

export type LiquidLevelMeasurementDirection = 'bottomUp' | 'topDown';

/** SensorTagId is the device_ID (device type) in IoT. */
export enum SensorTagId {
  RAIN_GAUGE = 109,
  DAVIS_WEATHER_STATION_RAIN_AU = 148,
  DAVIS_WEATHER_STATION_RAIN_US = 213,
  SPECTRUM_WEATHER_STATION_RAIN = 155,
  DAVIS_WEATHER_STATION_SOIL_MOISTURE_AU = 206,
  DAVIS_WEATHER_STATION_SOIL_TEMPERATURE_AU = 207,
  DAVIS_WEATHER_STATION_SOIL_MOISTURE_US = 219,
  DAVIS_WEATHER_STATION_SOIL_TEMPERATURE_US = 220,
  SPECTRUM_WEATHER_STATION_SOIL_MOISTURE = 210,
  SMT_100A_SOIL_MOISTURE = 145,
  SMT_100A_SOIL_TEMPERATURE = 146,
  XTEND_TROUGH = 136,
  TROUGH_DIRECT = 176,
  XTEND_HEATED_TROUGH_WATER_LEVEL = 183,
  XTEND_HEATED_TROUGH_STATUS_FLAGS = 184,
  XTEND_HEATED_TROUGH_MACHINE_CYCLES = 185,
  XTEND_HEATED_TROUGH_SUPPLY_VOLTS = 186,
  XTEND_HEATED_TROUGH_SUPPLY_CURRENT = 187,
  XTEND_HEATED_TROUGH_TEMPERATURE = 188,
  XTEND_HEATED_TROUGH_POWER = 189,
  // Diesel sensor tag is not used for conditional flows. Ref: utils/SamplePoints/devices/is-lite.ts
  DIESEL_LITE = 175,
  TANKBOT = 144,
  XTEND = 167,
  // Water Tank Lite sensor tag is not used for conditional flows. Ref: utils/SamplePoints/devices/is-lite.ts
  SOS = 856,
  PRIMARY_MACHINE_CONTROL = 860,
  SECONDARY_MACHINE_CONTROL = 863,
  LIQUID_FLOW_METER = 128,
  GAS_FLOW_METER = 228
}

export type SensorTypeNeedsOwnName =
  // See function 'isTroughDirect'
  | SensorTagId.TROUGH_DIRECT
  // See function 'onConsolidatedFeed'
  | SensorTagId.XTEND
  | SensorTagId.TANKBOT
  | SensorTagId.XTEND_TROUGH
  | SensorTagId.XTEND_HEATED_TROUGH_WATER_LEVEL;

export const SENSOR_NAME_BY_SENSOR_TAG_ID: Record<SensorTypeNeedsOwnName, string> = {
  [SensorTagId.TANKBOT]: 'TankBot',
  [SensorTagId.XTEND_TROUGH]: 'Xtend Trough',
  [SensorTagId.TROUGH_DIRECT]: 'Trough Direct',
  [SensorTagId.XTEND]: 'Xtend',
  [SensorTagId.XTEND_HEATED_TROUGH_WATER_LEVEL]: 'Xtend Heated Trough'
};

export const WEATHER_STATION_SOIL_MOISTURE_SENSOR_TAG_IDS = [
  SensorTagId.DAVIS_WEATHER_STATION_SOIL_MOISTURE_US,
  SensorTagId.DAVIS_WEATHER_STATION_SOIL_MOISTURE_AU,
  SensorTagId.SPECTRUM_WEATHER_STATION_SOIL_MOISTURE
];

export const WEATHER_STATION_SOIL_TEMPERATURE_SENSOR_TAG_IDS = [
  SensorTagId.DAVIS_WEATHER_STATION_SOIL_TEMPERATURE_US,
  SensorTagId.DAVIS_WEATHER_STATION_SOIL_TEMPERATURE_AU
];

export const SOIL_MOISTURE_SENSOR_TAG_IDS = [
  ...WEATHER_STATION_SOIL_MOISTURE_SENSOR_TAG_IDS,
  SensorTagId.SMT_100A_SOIL_MOISTURE
];

export const SOIL_TEMPERATURE_SENSOR_TAG_IDS = [
  ...WEATHER_STATION_SOIL_TEMPERATURE_SENSOR_TAG_IDS,
  SensorTagId.SMT_100A_SOIL_TEMPERATURE
];

export type SamplePointLastSampleData = Modify<
  Sample,
  { id: string; date: string; prevDate: string }
>;

export interface BaseSamplePoint {
  id: SamplePointId;
  sid: SamplePointSid;
  name?: string;
  assetTypeId: AssetTypeId;
  siteId: SiteId;
  deviceId: DeviceId;
  // Generated from selectSamplePoints()
  device?: Pick<
    Device,
    | 'id'
    | 'name'
    | 'backhaulType'
    | 'model'
    | 'serialNumber'
    | 'lastDataDate'
    | 'status'
    | 'lastStatusDate'
    | 'statusCause'
    | 'batteryLevel'
    | 'isBatteryLow'
    | 'fwVersion'
  >;

  location?: {
    coordinates: LatLong;
    /** True if the location belongs to a non-consolidated wireless device */
    isBridgeDeviceLocation: boolean;
  };

  siteName?: string;
  siteTimezoneCode?: string;

  lastSampleDate?: number;
  // TODO: Once remove the usage of lastSampleData in FE, should remove it from
  // BE too.
  /**
   * @deprecated Please use samplePointsStatistic.lastSample instead.
   * lastSampleData doesn't refresh often and its extraValues is not complete.
   */
  lastSampleData?: SamplePointLastSampleData;

  userComments: string | null;
  operatorComments?: string;

  isConfigured?: boolean;

  /**
   * The maximum time between samples in seconds.
   */
  maxPacketInterval?: number;

  deviceTags: {
    serialNumber: number;
    portNo: number;
    /** Generated from getPhysicalPortNoByLogicalPortNo() */
    physicalPortNo: number | string;
    internal: number;
    /**
     * Unlike device.isBatteryLow which signifies the battery status of the physical monitor,
     * this deviceTags.isBatteryLow signifies the battery status of Weather Station.
     * Expected to be true (if battery is low) on a Weather Station Controller sample point only.
     */
    isBatteryLow: boolean;
    model?: DeviceModel;
    channel: PowerMeterChannel | number;
  };
  // Sensor attributes. The id is the DeviceID in IoT layer (e.g., 167 means
  // Xtend), and it's the id of the node not hub. e.g.,
  // Water Level Sensor -> Xtend (node) -> Monitor (hub).
  sensorTags: {
    id: SensorTagId;
    type: string;
  };

  site?: Site;

  statusActive: number;

  status?: DeviceStatus | null; // From FRED
  lastStatusDate?: number; // From FRED
}

export interface SamplePointConfig {
  offset?: number;
  minValue?: number;
  maxValue?: number;
  totalCapacity?: number;
  flowDirection?: 'inflow' | 'outflow';
  tickConverter?: number;
  maxDepth?: number;
  totalBoreDepth?: number;
  maxVolume?: number;
  /** BE creates volume mapping for advanced dam and non-vertical tanks. */
  enabledVolumeMapping?: boolean;

  // Trough direct
  maxValueAutoCalibrated?: number;
  maxValueFromBluetoothApp?: number;

  levelDisplayUnit?: LevelDisplayUnit;
  volumeDisplayUnit?: VolumeDisplayUnit;
  liquidLevelMeasurementDirection?: 'bottomUp' | 'topDown';

  // Fuel tanks
  tankShape?: TankShape;
  tankWidth?: number;
  tankLength?: number;

  // Camera
  maxKb?: number;
  kbConsumed?: number;
  resetDate?: string | null;

  // Machine Control
  automation?: AutomaticControlType;
};

export type BridgeDevice = {
  id: typeof NON_CONSOLIDATED_WIRELESS_DEVICE_ID;
  name: string;
  serialNumber: string;
  status: DeviceStatus;
  statusCause: DeviceStatusCause;
  lastStatusDate: number;
  batteryLevel: Sample['rwValue'] | null;
  location: LatLong | null;

  // Current bridge devices use the Xtend platform and can connect to multiple monitors (single and multi pathing)
  // Because of this trait, they can have multiple backhauls
  // TODO: Add backhaulType field and handle existing usages in the codebase
};

export default interface SamplePoint extends BaseSamplePoint {
  config: SamplePointConfig;

  /** Computed by frontend, in selectSamplePoints(), to ease downstream logic. */
  bridgeDevice?: BridgeDevice

  /**
   * Water level related statistics that are calculated and attached on the
   * frontend.
   */
  waterLevelStatistics?: {
    changePerHour?: number;
    forecastedValue?: number;
    forecastedPercentFull?: number;
    maxValue?: number;
    forecastedVolume?: number;
    maxVolume?: number;
  };

  /**
   * Fuel level related statistics that are calculated and attached on the
   * frontend.
   */
  fuelLevelStatistics?: {
    capacity?: number;
    changePerHour?: number;
    height?: number;
    level?: number;
    maxVolume?: number;
    volume?: number;
  };

  /**
   * Liquid fertiliser related statistics that are calculated and attached on
   * the frontend.
   */
  liquidFertiliserStatistics?: {
    level: number;
    height: number;
    volume?: number;
    maxVolume?: number;
    capacity: number;
    /** Only exists on liquid fertiliser with forecastability */
    changePerHour?: number;
  };

  /**
   * Flow related statistics that are calculated and attached on the frontend.
   */
  flowStatistics?: {
    current?: number;
    oneDay?: number;
    sevenDays?: number;
    thirtyDays?: number;
  };

  /**
   * Rainfall related statistics that are calculated and attached on the
   * frontend.
   */
  rainfallStatistics?: {
    today: number | null;
    mtd: number | null;
    ytd: number | null;
    rolling90Days: number | null;
    rolling12Months: number | null;
    wetDaysYtd: number | null;
  };

  /**
   * Trough related statistics that are calculated and attached on the frontend.
   */
  troughStatistics?: {
    highestAverageThisFortnight?: number;
    minimumThisFortnight?: number;
    latestAverage?: number;
    latestMinimum?: number;
  };

  troughDirectStatistics?: {
    level: Quantity;
    capacity: Quantity;
  }

  /**
   * Soil related statistics that are calculated and attached on the frontend.
   */
  soilStatistics?: SoilSummary;

  /**
   * Line pressure related statistics that are calculated and attached on the
   * frontend.
   */
  linePressureStatistics?: {
    currentCapacity?: number;
    currentPressure?: number;
    maxPressure?: number;
  };

  /**
   * Dam related statistics that are calculated and attached on the frontend.
   */
  damStatistics?: {
    changePerHour?: number;
    /** In m/ft */
    level: number;
    reducedLevel?: number;
    /** When it's an advanced dam, the volume is the sum of slice volumes under
     * the reduced level.
     *
     * The volume is in display unit.
     * */
    volume?: number;
    /** In display unit */
    maxVolume?: number;
    /** In m/ft */
    maxDepth?: number;
    capacity?: number;
  };

  /**
   * Bore related statistics that are calculated and attached on the frontend.
   */
  boreStatistics?: {
    level?: number;
  };

  /**
   * Safety check-in related statistics that are calculated and attached on the
   * frontend.
   */
  safetyCheckInStatistics?: {
    checkInDate?: number;
    sosDate?: number;
  };

  machineControlStatistics?: MachineControl['machineControlStatistics'];

  /**
   * Summary metrics that are calculated and attached on the frontend for power meters.
   */
  powerStatistics?: {
    energyRolling7Days: number | null;
    energyToday: number | null;
    energyTrend7Days: number | null;
    lines: PowerLine[];
    powerMeterStatus: PowerMeterStatus;
    totalMeterReading: number | null;
  }

  weatherStatistics?: {
    lastSampleDate: number;
    /**
     * - For most weather sensors, this is the result of formatQuantity(lastSampleValueLocale, unitSymbol).
     * - For wind, this is a string combining wind speed and direction (e.g., "12 km/h (NW)").
     */
    lastSampleReading: string;
    /** When the weather station has a child rain sensor, attach rain summary from that sensor. */
    rainSummary?: RainSummary;
  };

  /**
   * Heater add-on derived statistics for display purposes.
   * Attached in selectSamplePoints when heater add-on is present and statistics available.
   */
  heaterStatistics?: HeaterSummary;

}

export interface MachineControl extends BaseSamplePoint {
  config: {
    automation?: AutomaticControlType;
  };

  _hidden?: {
    id: SamplePointId;
  };

  /** Computed by frontend, could be undefined when samplePoint data has resolved but samplePointsStatistic has not. */
  machineControlStatistics?: MachineControlStatistic & {
    verboseStatus: string;
    verboseAutomation: string;
  };

  /**
   * HACK:
   * Use `machineControlStatistics` whenever possible instead of this field.
   * This unstable field has been included in sample point requests for a while now but is not used anywhere yet.
   * The only use case for this is getting machine control related information when there's no acceptable way of
   * loading `machineControlStatistics` data.
   */
  machineControl: {
    /** More commonly known as `destinationSamplePoint.sid` in `MachineControlStatistic` */
    connectedToSamplePointSid: string | null;
    sourceSamplePointSid: string | null;
  } | null
}

export interface DamSamplePoint extends BaseSamplePoint {
  config: {
    offset?: number;
    maxDepth?: number;
    maxVolume?: number;
    enabledVolumeMapping?: boolean;
    levelDisplayUnit: LevelDisplayUnit;
    volumeDisplayUnit: VolumeDisplayUnit;
    liquidLevelMeasurementDirection: 'bottomUp' | 'topDown';
  };

  damStatistics: {
    changePerHour?: number;
    /** In m/ft */
    level: number;
    reducedLevel?: number;
    /** When it's an advanced dam, the volume is the sum of slice volumes under
     * the reduced level.
     *
     * The volume is in display unit.
     * */
    volume?: number;
    /** In display unit */
    maxVolume?: number;
    /** In m/ft */
    maxDepth?: number;
    capacity?: number;
  };
}

/**
 * A sample point that may or may not be supported by the frontend.
 */
export type RawSamplePoint = Modify<
  SamplePoint, // TODO: Should be BaseSamplePoint
  {
    assetTypeId: RawAssetTypeCode;
  }
>;

export interface MergedSamplePoint<H = SamplePoint> extends SamplePoint {
  _hidden?: H;
}

export type MergedWeatherStationSamplePoint = MergedSamplePoint<WeatherStationAssociatedSamplePoints>;

export type WeatherChildSensor = MergedSamplePoint<{ controllerSid: SamplePointSid; }>;

export type WeatherChildSensorWithPairedSamplePoint = MergedSamplePoint<{
  pairedSamplePointId: SamplePointId;
  controllerSid: SamplePointSid;
}>;

export interface SamplePointWithFlattenedNames extends SamplePoint {
  assetTypeName: string | typeof EM_DASH;
  deviceName?: string;
}

export interface SamplePointWithNonNullableLocation extends SamplePoint {
  location: NonNullable<SamplePoint['location']>;
}

export type WeatherStationAssociatedSamplePoints = {
  barometricPressure?: WeatherChildSensor;
  battery?: WeatherChildSensor;
  deltaT?: WeatherChildSensor;
  dewPoint?: WeatherChildSensorWithPairedSamplePoint;
  evapotranspiration?: WeatherChildSensor;
  humidity?: WeatherChildSensor;
  rainfall?: WeatherChildSensor;
  soilMoisture?: WeatherChildSensorWithPairedSamplePoint;
  soilTemperature?: WeatherChildSensorWithPairedSamplePoint;
  solarRadiation?: WeatherChildSensor;
  temperature?: WeatherChildSensorWithPairedSamplePoint;
  wind?: WeatherChildSensor;
};
